/**
 * useDialogDismiss — shared dialog a11y for the overlays.
 * On open: store the previously-focused element and focus the box named
 * by `initialFocusId` when it is given and present, else the dialog
 * container. Escape closes. On unmount: restore focus to the trigger.
 * Mirrors the focus-on-open / restore-on-close house pattern
 * (src/components/aiTools/DraftsList.tsx). Returns a ref to put on the
 * container (give it tabIndex={-1}).
 */

import { useEffect, useRef } from 'react';

export function useDialogDismiss<T extends HTMLElement>(onClose: () => void, initialFocusId?: string) {
  const ref = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const initial = initialFocusId === undefined ? null : document.getElementById(initialFocusId);
    (initial ?? ref.current)?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      // Focus trap: keep Tab within the dialog (aria-modal asserts the rest is inert).
      if (e.key === 'Tab' && ref.current) {
        const focusable = ref.current.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === ref.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, []);

  return ref;
}
